import IconFont from '@/common/IconFont';
import { Button, Popover, Checkbox } from 'antd';
import style from '../Analysis.module.less';
import {getColumnSetting, ColumnSettingParams, getSingleColumnSetting} from '../const';
import { useState, useMemo, useEffect } from 'react';

interface CoumnSettingProps {
  onColumnChange?: (checked: boolean, dataIndex: string, newSetting?: ColumnSettingParams[]) => void;
  onReset?: (checked: boolean, dataIndex: string[]) => void;
  showGradeLine: boolean;
  isSingle?: boolean;
  isJunior?: boolean;
  storageKey?: string;
  storageVersion?: number;
  sessionStorageKey?: string;
}

interface ColumnSettingStorage {
  version?: number;
  columns?: Record<string, boolean>;
}

export const getCachedColumnSetting = (
  key: string | undefined,
  setting: ColumnSettingParams[],
  version = 1
) => {
  if (!key) return setting;
  try {
    const cache = JSON.parse(localStorage.getItem(key) || '{}') as ColumnSettingStorage;
    if (cache.version !== version) return setting;
    const columns = cache.columns || {};
    return setting.map(i => ({
      ...i,
      active: i.disabled ? i.active : columns[i.dataIndex] ?? i.active
    }));
  } catch {
    return setting;
  }
};

export const saveColumnSettingCache = (
  key: string | undefined,
  setting: ColumnSettingParams[],
  version = 1
) => {
  if (!key) return;
  try {
    const columns = setting.reduce((a, c) => {
      return {
        ...a,
        [c.dataIndex]: c.active
      };
    }, {} as Record<string, boolean>);
    localStorage.setItem(key, JSON.stringify({
      version,
      columns
    }));
  } catch {
    return;
  }
};

const ColumnSetting: React.FC<CoumnSettingProps> = props => {

  const [setting, setSetting] = useState<ColumnSettingParams[]>([]);
  const cacheKey = props.storageKey || props.sessionStorageKey;
  const storageVersion = props.storageVersion || 1;

  useEffect(() => {
    const defaultSetting = props.isSingle ? getSingleColumnSetting() :getColumnSetting({
      showGradeLine: props.showGradeLine,
      isJunior: props.isJunior
    });
    setSetting(getCachedColumnSetting(cacheKey, defaultSetting, storageVersion));
  }, [props.showGradeLine, props.isSingle, props.isJunior, cacheKey, storageVersion]);

  const halfCheck = useMemo(() => {
    return setting.some(i => !i.active);
  }, [setting]);

  const allChecked = useMemo(() => {
    return setting.every(i => i.active);
  }, [setting]);

  const allCheckedChange = () => {
    const newSetting = setting.map(i => {
      if (i.disabled) {
        return i;
      } else {
        return {
          ...i,
          active: halfCheck
        };
      }
    });
    setSetting(newSetting);
    saveColumnSettingCache(cacheKey, newSetting, storageVersion);
    props.onReset && props.onReset(halfCheck, setting.filter(i => !i.disabled ).map(i => i.dataIndex));
  };

  const reset = () => {
    const set = props.isSingle ? getSingleColumnSetting() :getColumnSetting({
      showGradeLine: props.showGradeLine,
      isJunior: props.isJunior
    });
    setSetting(set);
    saveColumnSettingCache(cacheKey, set, storageVersion);
    if (props.onReset) {
      const activeDataIndex = set.filter(i => !i.disabled && i.active).map(i => i.dataIndex);
      const inactiveDataIndex = set.filter(i => !i.disabled && !i.active).map(i => i.dataIndex);
      if (activeDataIndex.length > 0) props.onReset(true, activeDataIndex);
      if (inactiveDataIndex.length > 0) props.onReset(false, inactiveDataIndex);
    }
  };

  const Header = (
    <div className={style.ColumnSettingHeader}>
      <Checkbox
        indeterminate={halfCheck}
        checked={allChecked}
        onChange={allCheckedChange}
      >
        列设置
      </Checkbox>
      <a onClick={reset}>重置</a>
    </div>
  );

  const checkboxChange = (checked: boolean, dataIndex: string) => {
    const newSetting = setting.map(i => {
      if (i.dataIndex === dataIndex) {
        return {
          ...i,
          active: checked
        };
      } else {
        return i;
      }
    });
    setSetting(newSetting);
    saveColumnSettingCache(cacheKey, newSetting, storageVersion);
    props.onColumnChange && props.onColumnChange(checked, dataIndex, newSetting);
  };

  const Content = (
    <div className={style.columnSettingContent}>
      {
        setting.map(i => (
          <div key={i.title}>
            <Checkbox
              checked={i.active}
              disabled={i.disabled}
              onChange={e => checkboxChange(e.target.checked, i.dataIndex)}
            >
              {i.title}
            </Checkbox>
          </div>
        ))
      }
    </div>
  );

  return (
    <Popover
      placement="bottomRight"
      title={Header}
      content={Content}
      trigger="click"
    >
      <Button icon={<IconFont type="icon-shezhi" />}>列设置</Button>
    </Popover>
  );
};

export default ColumnSetting;
